/**
 * F39 (Wave 2C) — admin placement-approval FIFO queue.
 *
 * Route: `/placement-approvals`
 * Permission: `placement_approvals:review` (super_admin + content_moderator).
 *
 * Default view = the FIFO `pending_approval` queue (oldest first). A Chip-row
 * filter switches to scheduled / rejected / all views (backed by the
 * cross-venue placements list). Each row links to the detail page where the
 * creative is previewed + Approve/Reject happen. The sidebar pending-count
 * badge is polled separately by `NavCountsProvider`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Chip,
  CurrencyDisplay,
  EmptyState,
  Pagination,
  PlacementStateBadge,
  Skeleton,
  StatusChip,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { isPlacementBadgeState } from '../placements/placementBadge';
import {
  adminPlacementsApi,
  type AdminPlacementRow,
  type ListPlacementsOpts,
} from '../../api/placements';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

const PAGE_SIZE = 25;

type UiFilter = 'pending_approval' | 'scheduled' | 'rejected' | 'all';
const DEFAULT_FILTER: UiFilter = 'pending_approval';

const FILTER_TABS: ReadonlyArray<{ value: UiFilter; labelKey: string; defaultLabel: string }> = [
  {
    value: 'pending_approval',
    labelKey: 'placement:admin.approvals.tab_pending',
    defaultLabel: 'Pending',
  },
  {
    value: 'scheduled',
    labelKey: 'placement:admin.approvals.tab_scheduled',
    defaultLabel: 'Scheduled',
  },
  {
    value: 'rejected',
    labelKey: 'placement:admin.approvals.tab_rejected',
    defaultLabel: 'Rejected',
  },
  { value: 'all', labelKey: 'placement:admin.approvals.tab_all', defaultLabel: 'All' },
];

function formatPeriod(periodStart: string, periodEnd: string, locale: string): string {
  try {
    const fmt = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Beirut',
    });
    return `${fmt.format(new Date(periodStart))} – ${fmt.format(new Date(periodEnd))}`;
  } catch {
    return `${periodStart} – ${periodEnd}`;
  }
}

export function PlacementApprovalsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterParam = (searchParams.get('status') as UiFilter | null) ?? DEFAULT_FILTER;
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [rows, setRows] = useState<readonly AdminPlacementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (filterParam === 'pending_approval') {
        // FIFO queue endpoint (oldest first).
        const data = await adminPlacementsApi.listApprovals({
          page: pageParam,
          perPage: PAGE_SIZE,
        });
        setRows(data.data);
        setTotal(data.pagination.total);
      } else {
        const opts: ListPlacementsOpts = {
          page: pageParam,
          perPage: PAGE_SIZE,
          ...(filterParam !== 'all' ? { status: filterParam } : {}),
        };
        const data = await adminPlacementsApi.listPlacements(opts);
        setRows(data.data);
        setTotal(data.pagination.total);
      }
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [filterParam, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onFilterChange = (value: UiFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_FILTER) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<AdminPlacementRow>[]>(
    () => [
      {
        key: 'venue',
        header: t('placement:admin.approvals.col_venue', { defaultValue: 'Venue' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-semibold text-ink">
              {row.venueName ?? row.venueId}
            </span>
            {row.venueSlug ? (
              <span className="font-mono text-[11px] text-ink-3">{row.venueSlug}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'type',
        header: t('placement:admin.approvals.col_type', { defaultValue: 'Type' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12.5px] text-ink">
              {t(`placement:type.${row.placementType}`, { defaultValue: row.placementType })}
            </span>
            {row.scopeSport || row.scopeCityCode ? (
              <span className="text-[11px] text-ink-3">
                {[row.scopeSport, row.scopeCityCode].filter(Boolean).join(' · ')}
              </span>
            ) : null}
            {row.creativeImageS3Key || row.creativeCopy ? (
              <Badge tone="info" size="sm" data-testid={`approval-creative-${row.id}`}>
                {t('placement:admin.approvals.has_creative', { defaultValue: 'Creative' })}
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: 'period',
        header: t('placement:admin.approvals.col_period', { defaultValue: 'Period' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {formatPeriod(row.periodStart, row.periodEnd, locale)} · {row.weeks}w
          </span>
        ),
      },
      {
        key: 'charge',
        header: t('placement:admin.approvals.col_charge', { defaultValue: 'Net charge' }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay
            amount={row.netChargeMinor}
            currency={row.priceCurrency}
            locale={locale}
          />
        ),
      },
      {
        key: 'status',
        header: t('placement:admin.approvals.col_status', { defaultValue: 'Status' }),
        cell: (row) =>
          isPlacementBadgeState(row.status) ? (
            <PlacementStateBadge
              size="sm"
              state={row.status}
              label={t(`placement:status.${row.status}`, { defaultValue: row.status })}
            />
          ) : (
            <StatusChip
              size="sm"
              status="rejected"
              label={t(`placement:status.${row.status}`, { defaultValue: row.status })}
            />
          ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="placement-approvals-page">
      <PageHeader
        title={t('placement:admin.approvals.title', { defaultValue: 'Placement approvals' })}
        subtitle={t('placement:admin.approvals.subtitle', {
          defaultValue:
            'Pre-approve every placement creative + copy before it goes live. Oldest pending first.',
        })}
      />

      <div
        role="tablist"
        aria-label={t('placement:admin.approvals.filter_label', {
          defaultValue: 'Filter by status',
        })}
        className="flex flex-wrap items-center gap-2"
      >
        {FILTER_TABS.map((tab) => (
          <Chip
            key={tab.value}
            selected={filterParam === tab.value}
            data-testid={`placement-approvals-tab-${tab.value}`}
            onClick={() => onFilterChange(tab.value)}
          >
            {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
          </Chip>
        ))}
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="placement-approvals-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="receipt" size={36} color="currentColor" />}
          title={t('placement:admin.approvals.queue_empty', {
            defaultValue: 'No placements awaiting approval.',
          })}
        />
      ) : (
        <>
          <Table
            data={rows as AdminPlacementRow[]}
            columns={columns as TableColumn<AdminPlacementRow>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/placement-approvals/${row.id}`)}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={pageParam}
                pageCount={pageCount}
                onChange={onPageChange}
                ariaLabel={t('placement:admin.approvals.title', {
                  defaultValue: 'Approvals pagination',
                })}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default PlacementApprovalsPage;
